// src/contexts/AuthContext.jsx

import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { auth, db } from '../firebase.js'; // Updated to include .js extension
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // New error state

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setError(null); // Reset error state on auth state change

      try {
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();

            if (userData.companyId) {
              setCompanyId(userData.companyId);
              setCurrentUser({ ...user, companyId: userData.companyId });
            } else {
              console.error('No company ID associated with this user.');
              setError('No company ID found. Please contact support.');
              setCurrentUser(null);
            }
          } else {
            console.error('User document does not exist.');
            setError('User data not found. Please contact support.');
            setCurrentUser(null);
          }
        } else {
          // User is signed out
          setCurrentUser(null);
          setCompanyId(null);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('An error occurred while fetching user data.');
        setCurrentUser(null);
        setCompanyId(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Optional: Logout function
  const logout = () => auth.signOut();

  const value = useMemo(
    () => ({
      currentUser,
      companyId,
      loading,
      error,       // Exposed error state
      logout,      // Exposed logout function
    }),
    [currentUser, companyId, loading, error]
  );

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="loading-indicator">Loading...</div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};